import { motion } from "framer-motion";
import { useEffect, useState, useRef } from "react";

type SectionKey =
  | "turnusy"
  | "co-si-pribalit"
  | "organizator"
  | "kontakt"
  | "aktivity"
  | "kde-nas-najdes";

const InfoContact = () => {
  const sectionVariants = {
    hidden: { opacity: 0, y: 100 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const contentVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 1 } },
  };

  const childVariants = {
    animate: {
      y: [0, -20, 0],
      transition: { duration: 3, repeat: Infinity, ease: "easeInOut" },
    },
  };

  const textVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, staggerChildren: 0.2 },
    },
  };

  const letterVariants = {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.3 } },
  };

  const characterVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
  };

  const textAnimationVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  };

  const [highlightedItemPack, setHighlightedItemPack] = useState(0);
  const [highlightedItemActivity, setHighlightedItemActivity] = useState(0);
  const [activeSection, setActiveSection] = useState<SectionKey>("turnusy");
  const [isSectionVisible, setIsSectionVisible] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);

  // const sectionTexts: Record<SectionKey, string[]> = {
  //   turnusy: ["Hurá na tábor!", "Tešíme sa!"],
  //   "co-si-pribalit": ["Spacák nesmie chýbať!", "Zbaľ si šiltovku!"],
  //   organizator: ["Svätý Vendelín volá!", "Zubák je náš domov!"],
  //   kontakt: ["Máš otázky? Neváhaj sa ozvať!", "Zavolaj na tábor!"],
  //   aktivity: ["Táborák večer!", "Poď na turistiku!"],
  //   "kde-nas-najdes": ["Zubák 140 čaká!", "Nájdi nás v prírode!"],
  // };

  useEffect(() => {
    const packInterval = setInterval(() => {
      setHighlightedItemPack((prev) => (prev + 1) % 6);
    }, 3000);
    const activityInterval = setInterval(() => {
      setHighlightedItemActivity((prev) => (prev + 1) % 6);
    }, 3000);

    // // Text interval based on active section
    // const textInterval = setInterval(() => {
    //   // const texts = sectionTexts[activeSection];
    //   // Alternate between the two texts for variety
    //   // setRandomText1(texts[0]);
    //   // setRandomText2(texts[1]);
    // }, 4000);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSectionVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const handleScroll = () => {
      const sections: SectionKey[] = [
        "turnusy",
        "co-si-pribalit",
        "organizator",
        "kontakt",
        "aktivity",
        "kde-nas-najdes",
      ];
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { top, bottom } = element.getBoundingClientRect();
          const sectionTop = top + window.scrollY;
          const sectionBottom = bottom + window.scrollY;

          if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
            setActiveSection(section);
            // Immediately update texts when section changes
            // setRandomText1(sectionTexts[section][0]);
            // setRandomText2(sectionTexts[section][1]);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      clearInterval(packInterval);
      clearInterval(activityInterval);
      // clearInterval(textInterval);
      window.removeEventListener("scroll", handleScroll);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [activeSection]);

  const characterImages: Record<SectionKey, string[]> = {
    turnusy: [
      "./characters_new/hura-01.svg",
      "./characters_new/dobrodruzstvo-01.svg",
    ],
    "co-si-pribalit": [
      "./characters_new/spacak-01.svg",
      "./characters_new/siltovka-01.svg",
    ],
    organizator: [
      "./characters_new/vendelin-01.svg",
      "./characters_new/spomienky-01.svg",
    ],
    kontakt: [
      "./characters_new/otazka-01.svg",
      "./characters_new/radost-01.svg",
    ],
    aktivity: [
      "./characters_new/taborak-01.svg",
      "./characters_new/ruky-01.svg",
    ],
    "kde-nas-najdes": [
      "./characters_new/priroda-01.svg",
      "./characters_new/tesime_sa-01.svg",
    ],
  };

  return (
    <section
      id="info-contact"
      ref={sectionRef}
      className="relative bg-cream text-navy w-full overflow-x-hidden overflow-y-hidden"
    >
      {isSectionVisible && (
        <motion.div
          className="hidden sm:block fixed right-0 top-1/4 transform -translate-x-1/2 -translate-y-1/2 z-[1000] flex flex-col items-center"
          key={activeSection}
          initial="hidden"
          animate="visible"
          variants={characterVariants}
        >
          <div className="relative">
            <img
              src={characterImages[activeSection][0]}
              alt="Character 1"
              className="h-24 sm:h-32 md:h-48 lg:h-48 xl:h-64 2xl:h-64 w-24 sm:w-32 md:w-48 lg:w-48 xl:w-64 2xl:w-64 drop-shadow-xl"
            />
            {/* <motion.div
              className="absolute top-1/2 left-0 w-full text-center text-sm sm:text-base md:text-lg lg:text-xl text-primary font-bold  rounded-lg p-1 sm:p-2 z-[1100]"
              initial="hidden"
              animate="visible"
              variants={textAnimationVariants}
              key={randomText1}
            >
              {randomText1}
            </motion.div> */}
          </div>
          <div className="relative mt-4">
            <img
              src={characterImages[activeSection][1]}
              alt="Character 2"
              className="h-24 sm:h-32 md:h-48 lg:h-48 xl:h-64 2xl:h-64 w-24 sm:w-32 md:w-48 lg:w-48 xl:w-64 2xl:w-64 drop-shadow-xl"
            />
            {/* <motion.div
              className="absolute top-1/2 left-0 w-full text-center text-sm sm:text-base md:text-lg lg:text-xl text-primary font-bold  rounded-lg p-1 sm:p-2 z-[1100]"
              initial="hidden"
              animate="visible"
              variants={textAnimationVariants}
              key={randomText2}
            >
              {randomText2}
            </motion.div> */}
          </div>
        </motion.div>
      )}

      {/* Turnusy Section */}
      <div
        id="turnusy"
        className="min-h-[80vh] md:h-screen flex items-center justify-center top-0 relative overflow-x-hidden overflow-y-hidden py-4 sm:py-6 md:py-8 z-10"
      >
        <motion.div
          className="text-center z-10 px-4 sm:px-6 md:px-8 w-full max-w-[90vw] sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-5xl 2xl:max-w-6xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <motion.h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-8xl font-bold uppercase tracking-tight text-primary drop-shadow-2xl"
            style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
            initial={{ scale: 0.5 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
          >
            Turnusy
          </motion.h2>
          <motion.div
            className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 xl:mt-10 2xl:mt-12 relative"
            variants={contentVariants}
          >
            <motion.div className="bg-white p-4 sm:p-6 md:p-8 lg:p-10 xl:p-10 2xl:p-12 rounded-3xl shadow-2xl border-4 border-secondary w-full max-w-[95%] sm:max-w-[90%] md:max-w-[85%] lg:max-w-[80%] xl:max-w-[75%] 2xl:max-w-4xl mx-auto relative">
              <motion.div
                className="absolute -top-4 sm:-top-6 md:-top-6 lg:-top-8 xl:-top-8 2xl:-top-8 -left-4 sm:-left-6 md:-left-6 lg:-left-8 xl:-left-8 2xl:-left-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="absolute -bottom-4 sm:-bottom-6 md:-bottom-6 lg:-bottom-8 xl:-bottom-8 2xl:-bottom-8 -right-4 sm:-right-6 md:-right-6 lg:-right-8 xl:-right-8 2xl:-right-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-secondary rounded-full"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.img
                src="https://img.icons8.com/fluency/48/star.png"
                alt="Star"
                className="absolute top-0 left-0 h-6 sm:h-8 md:h-8 lg:h-10 xl:h-10 2xl:h-12"
                animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
              <motion.img
                src="https://img.icons8.com/fluency/48/star.png"
                alt="Star"
                className="absolute bottom-0 right-0 h-6 sm:h-8 md:h-8 lg:h-10 xl:h-10 2xl:h-12"
                animate={{ rotate: [0, -360], scale: [1, 1.2, 1] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
              <motion.ul
                className="space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-5 xl:space-y-5 2xl:space-y-6 text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
                variants={textVariants}
                initial="initial"
                animate="animate"
              >
                <motion.li
                  className="bg-cream p-2 sm:p-3 md:p-4 lg:p-4 xl:p-4 2xl:p-4 rounded-xl shadow-lg border-2 border-primary"
                  variants={letterVariants}
                >
                  <span className="font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl text-navy">
                    1.7. - 6.7.
                  </span>{" "}
                  <span className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-primary">
                    (5 nocí, 6 dní)
                  </span>
                  : Začíname{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-navy">
                    večerou o 17:00
                  </span>
                  , príchod od 15:00 do 16:00, končíme{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-navy">
                    obedom o 13:00
                  </span>{" "}
                  – iba za{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-primary">
                    249,99 €
                  </span>
                  !
                </motion.li>
                <motion.li
                  className="bg-cream p-2 sm:p-3 md:p-4 lg:p-4 xl:p-4 2xl:p-4 rounded-xl shadow-lg border-2 border-primary"
                  variants={letterVariants}
                >
                  <span className="font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl text-navy">
                    5.8. - 9.8.
                  </span>{" "}
                  <span className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-primary">
                    (4 noci, 5 dní)
                  </span>
                  : Začíname{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-navy">
                    večerou o 18:00
                  </span>
                  , príchod od 16:00 do 17:00, končíme{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-navy">
                    obedom o 13:00
                  </span>{" "}
                  – iba za{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-primary">
                    219,99 €
                  </span>
                  !
                </motion.li>
                <motion.li
                  className="bg-cream p-2 sm:p-3 md:p-4 lg:p-4 xl:p-4 2xl:p-4 rounded-xl shadow-lg border-2 border-primary"
                  variants={letterVariants}
                >
                  <span className="font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl text-navy">
                    10.8. - 15.8.
                  </span>{" "}
                  <span className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-primary">
                    (5 nocí, 6 dní)
                  </span>
                  : Začíname{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-navy">
                    večerou o 17:00
                  </span>
                  , príchod od 15:00 do 16:00, končíme{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-navy">
                    obedom o 13:00
                  </span>{" "}
                  – iba za{" "}
                  <span className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl text-primary">
                    249,99 €
                  </span>
                  !
                </motion.li>
              </motion.ul>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.img
          src="/assets/superman-01.svg"
          alt="Children"
          className="absolute bottom-6 sm:bottom-8 md:bottom-10 lg:bottom-12 xl:bottom-14 2xl:bottom-16 left-4 sm:left-8 md:left-12 lg:left-16 xl:left-16 2xl:left-28 h-5 sm:h-6 md:h-8 lg:h-8 xl:h-10 2xl:h-32 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.img
          src="/assets/lopty.svg"
          alt="Children"
          className="absolute top-6 sm:top-8 md:top-10 lg:top-12 xl:top-14 2xl:top-16 right-4 sm:right-8 md:right-12 lg:right-16 xl:right-16 2xl:right-20 h-12 sm:h-16 md:h-20 lg:h-24 xl:h-28 2xl:h-32 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.div
          className="absolute top-4 sm:top-6 md:top-8 lg:top-8 xl:top-10 2xl:top-10 left-4 sm:left-6 md:left-8 lg:left-10 xl:left-10 2xl:left-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-primary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-4 sm:bottom-6 md:bottom-8 lg:bottom-8 xl:bottom-10 2xl:bottom-10 right-4 sm:right-6 md:right-8 lg:right-10 xl:right-10 2xl:right-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-secondary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </div>

      {/* Čo si pribaliť Section */}
      <div
        id="co-si-pribalit"
        className="min-h-[80vh] md:h-screen flex items-center justify-center sticky top-0 relative overflow-x-hidden overflow-y-hidden py-4 sm:py-6 md:py-8"
      >
        <motion.div
          className="text-center z-10 px-4 sm:px-6 md:px-8 w-full max-w-[90vw] sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-5xl 2xl:max-w-6xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <motion.h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-8xl font-bold uppercase tracking-tight text-primary drop-shadow-2xl"
            style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
            initial={{ scale: 0.5 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
          >
            Čo si pribaliť
          </motion.h2>
          <motion.div
            className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 xl:mt-10 2xl:mt-12 relative"
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="bg-white h-full md:h-[33rem] p-4 sm:p-6 md:p-8 lg:p-10 xl:p-6 2xl:p-6 rounded-3xl shadow-2xl border-4 border-primary w-full max-w-[95%] sm:max-w-[90%] md:max-w-[80%] lg:max-w-[75%] xl:max-w-[70%] 2xl:max-w-[55vw]  mx-auto flex flex-col sm:flex-row items-center gap-4 sm:gap-6 md:gap-8 lg:gap-10 xl:gap-12 2xl:gap-12">
              <motion.div
                className="absolute -top-4 sm:-top-6 md:-top-6 lg:-top-8 xl:-top-8 2xl:-top-8 -left-4 sm:-left-6 md:-left-6 lg:-left-8 xl:-left-8 2xl:-left-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="absolute -bottom-4 sm:-bottom-6 md:-bottom-6 lg:-bottom-8 xl:-bottom-8 2xl:-bottom-8 -right-4 sm:-right-6 md:-right-6 lg:-right-8 xl:-right-8 2xl:-right-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-secondary rounded-full"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.ul className="w-full sm:w-1/2 text-left text-xs sm:text-base md:text-md lg:text-md xl:text-md 2xl:text-lg space-y-2 sm:space-y-3 md:space-y-3 lg:space-y-4 xl:space-y-4 2xl:space-y-4">
                {[
                  "Vankúš a deku alebo spacák na pohodlný spánok",
                  "Fľašu na vodu, aby si zostal hydratovaný",
                  "Papuče a šiltovku pre pohodlie a slnko",
                  "Vreckovky a ruksak na drobnosti",
                  "Pršiplášť, ak by nás prekvapil dážď",
                  "Prefotenú kartičku poistenca ( pre istotu )",
                ].map((text, index) => (
                  <motion.li
                    key={index}
                    className={`p-2 sm:p-3 md:p-4 lg:p-4 xl:p-4 2xl:p-4 rounded-xl transition-all duration-300 ${
                      highlightedItemPack === index
                        ? "bg-primary text-cream"
                        : "bg-cream"
                    }`}
                    onMouseEnter={() => setHighlightedItemPack(index)}
                  >
                    {text}
                  </motion.li>
                ))}
              </motion.ul>
              <motion.div className="w-full sm:w-1/2 flex justify-center">
                <motion.img
                  src={
                    [
                      "/assets/vankus.svg",
                      "/assets/flasa.svg",
                      "/assets/siltovkaa-01.svg",
                      "/assets/ruksak.svg",
                      "/assets/prsiplast-01.svg",
                      "/assets/karticka-01.svg",
                    ][highlightedItemPack]
                  }
                  alt="Item Icon"
                  className="h-24 sm:h-32 md:h-36 lg:h-40 xl:h-44 2xl:h-48 drop-shadow-xl max-w-full"
                  key={highlightedItemPack}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
              </motion.div>
              <motion.img
                src="https://img.icons8.com/fluency/48/star.png"
                alt="Star"
                className="absolute top-1 sm:top-1 md:top-2 lg:top-2 xl:top-2 2xl:top-2 right-1 sm:right-1 md:right-2 lg:right-2 xl:right-2 2xl:right-2 h-5 sm:h-6 md:h-6 lg:h-8 xl:h-8 2xl:h-8"
                animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.img
          src="/assets/pastoracne_centrum.svg"
          alt="Children"
          className="absolute bottom-6 sm:bottom-8 md:bottom-10 lg:bottom-12 xl:bottom-14 2xl:bottom-16 left-4 sm:left-8 md:left-12 lg:left-16 xl:left-16 2xl:left-20 h-5 sm:h-6 md:h-8 lg:h-8 xl:h-10 2xl:h-20 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.img
          src="/assets/lopty.svg"
          alt="Children"
          className="absolute top-6 sm:top-8 md:top-10 lg:top-12 xl:top-14 2xl:top-16 right-4 sm:right-8 md:right-12 lg:right-16 xl:right-16 2xl:right-20 h-12 sm:h-16 md:h-20 lg:h-24 xl:h-28 2xl:h-32 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.div
          className="absolute top-4 sm:top-6 md:top-8 lg:top-8 xl:top-10 2xl:top-10 left-4 sm:left-6 md:left-8 lg:left-10 xl:left-10 2xl:left-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-primary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-4 sm:bottom-6 md:bottom-8 lg:bottom-8 xl:bottom-10 2xl:bottom-10 right-4 sm:right-6 md:right-8 lg:right-10 xl:right-10 2xl:right-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-secondary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </div>

      {/* Organizátor Section */}
      <div
        id="organizator"
        className="min-h-[80vh] md:h-screen flex items-center justify-center sticky top-0 relative overflow-x-hidden overflow-y-hidden py-4 sm:py-6 md:py-8"
      >
        <motion.div
          className="text-center z-10 px-4 sm:px-6 md:px-8 w-full max-w-[90vw] sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-5xl 2xl:max-w-6xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <motion.h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-8xl font-bold uppercase tracking-tight text-primary drop-shadow-2xl"
            style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
            initial={{ scale: 0.5 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
          >
            Organizátor
          </motion.h2>
          <motion.div
            className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 xl:mt-10 2xl:mt-12 relative"
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="bg-white p-4 sm:p-6 md:p-8 lg:p-10 xl:p-8 2xl:p-8 rounded-3xl shadow-2xl border-4 border-secondary w-full max-w-[95%] sm:max-w-[90%] md:max-w-[85%] lg:max-w-[80%] xl:max-w-[75%] 2xl:max-w-4xl mx-auto relative">
              <motion.div
                className="absolute -top-4 sm:-top-6 md:-top-6 lg:-top-8 xl:-top-8 2xl:-top-8 -left-4 sm:-left-6 md:-left-6 lg:-left-8 xl:-left-8 2xl:-left-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="absolute -bottom-4 sm:-bottom-6 md:-bottom-6 lg:-bottom-8 xl:-bottom-8 2xl:-bottom-8 -right-4 sm:-right-6 md:-right-6 lg:-right-8 xl:-right-8 2xl:-right-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-secondary rounded-full"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="flex flex-col items-center space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-5 xl:space-y-5 2xl:space-y-6"
                variants={textVariants}
                initial="initial"
                animate="animate"
              >
                <motion.p
                  variants={letterVariants}
                  className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl font-bold text-primary"
                >
                  Ahoj, toto sme my!
                </motion.p>
                <motion.p
                  variants={letterVariants}
                  className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl leading-relaxed"
                >
                  Tábor organizuje{" "}
                  <span className="font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl text-navy">
                    Občianske združenie Svätý Vendelín
                  </span>
                </motion.p>
                <motion.div
                  className="bg-cream p-2 sm:p-3 md:p-4 lg:p-5 xl:p-5 2xl:p-6 rounded-xl shadow-lg border-2 border-primary w-full"
                  variants={letterVariants}
                >
                  <motion.p
                    variants={letterVariants}
                    className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl mt-1 sm:mt-2"
                  >
                    Adresa: Zubák 140, 020 64 Zubák
                  </motion.p>
                  <motion.p
                    variants={letterVariants}
                    className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
                  >
                    IČO: 45783128
                  </motion.p>
                  <motion.p
                    variants={letterVariants}
                    className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
                  >
                    DIČ: 2120155169
                  </motion.p>
                  <motion.p
                    variants={letterVariants}
                    className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
                  >
                    ČSOB - Československá obchodná banka, a.s.
                  </motion.p>
                  <motion.p
                    variants={letterVariants}
                    className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
                  >
                    Kód banky: 7500
                  </motion.p>
                  <motion.p
                    variants={letterVariants}
                    className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
                  >
                    SWIFT (BIC) kód: CEKOSKBX
                  </motion.p>
                  <motion.p
                    variants={letterVariants}
                    className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
                  >
                    IBAN: SK09 7500 0000 0040 2249 0459
                  </motion.p>
                </motion.div>
                <motion.p
                  variants={letterVariants}
                  className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl leading-relaxed"
                >
                  v spolupráci so {""}
                  <span className="font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl text-navy">
                    Žilinskou diecézou.
                  </span>
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.img
          src="/assets/ohnisko-01.svg"
          alt="Children"
          className="absolute bottom-6 sm:bottom-8 md:bottom-10 lg:bottom-12 xl:bottom-14 2xl:bottom-16 left-4 sm:left-8 md:left-12 lg:left-16 xl:left-16 2xl:left-48 h-5 sm:h-6 md:h-8 lg:h-8 xl:h-10 2xl:h-20 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.img
          src="/assets/lopty.svg"
          alt="Children"
          className="absolute top-6 sm:top-8 md:top-10 lg:top-12 xl:top-14 2xl:top-16 right-4 sm:right-8 md:right-12 lg:right-16 xl:right-16 2xl:right-20 h-12 sm:h-16 md:h-20 lg:h-24 xl:h-28 2xl:h-32 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.div
          className="absolute top-4 sm:top-6 md:top-8 lg:top-8 xl:top-10 2xl:top-10 left-4 sm:left-6 md:left-8 lg:left-10 xl:left-10 2xl:left-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-primary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-4 sm:bottom-6 md:bottom-8 lg:bottom-8 xl:bottom-10 2xl:bottom-10 right-4 sm:right-6 md:right-8 lg:right-10 xl:right-10 2xl:right-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-secondary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </div>

      {/* Kontakt Section */}
      <div
        id="kontakt"
        className="min-h-[80vh] md:h-screen flex items-center justify-center sticky top-0 relative overflow-x-hidden overflow-y-hidden py-4 sm:py-6 md:py-8"
      >
        <motion.div
          className="text-center z-10 px-4 sm:px-6 md:px-8 w-full max-w-[90vw] sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-5xl 2xl:max-w-6xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <motion.h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-8xl font-bold uppercase tracking-tight text-primary drop-shadow-2xl"
            style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
            initial={{ scale: 0.5 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
          >
            Kontakt
          </motion.h2>
          <motion.div
            className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 xl:mt-10 2xl:mt-12 relative"
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="bg-white p-4 sm:p-6 md:p-8 lg:p-10 xl:p-10 2xl:p-12 rounded-3xl shadow-2xl border-4 border-primary w-full max-w-[95%] sm:max-w-[90%] md:max-w-[85%] lg:max-w-[80%] xl:max-w-[75%] 2xl:max-w-4xl mx-auto relative">
              <motion.div
                className="absolute -top-4 sm:-top-6 md:-top-6 lg:-top-8 xl:-top-8 2xl:-top-8 -left-4 sm:-left-6 md:-left-6 lg:-left-8 xl:-left-8 2xl:-left-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="absolute -bottom-4 sm:-bottom-6 md:-bottom-6 lg:-bottom-8 xl:-bottom-8 2xl:-bottom-8 -right-4 sm:-right-6 md:-right-6 lg:-right-8 xl:-right-8 2xl:-right-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-secondary rounded-full"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 md:space-x-6 lg:space-x-8 xl:space-x-8 2xl:space-x-8"
                variants={textVariants}
                initial="initial"
                animate="animate"
              >
                <motion.div className="text-left w-full sm:w-auto">
                  <motion.p
                    variants={letterVariants}
                    className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl font-bold text-navy"
                  >
                    Duchovný správca:
                  </motion.p>
                  <motion.p
                    variants={letterVariants}
                    className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl text-primary"
                  >
                    Mgr. Vladimír Tomáš PhD.
                  </motion.p>
                  <motion.p
                    variants={letterVariants}
                    className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-2xl font-bold text-navy mt-1 sm:mt-2 md:mt-3 lg:mt-4 xl:mt-4 2xl:mt-4"
                  >
                    Email:
                  </motion.p>
                  <motion.a
                    href="mailto:dieceza.zilina@dieceznytabor.sk"
                    className="underline text-primary font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl"
                    variants={letterVariants}
                  >
                    dieceza.zilina@dieceznytabor.sk
                  </motion.a>
                  <motion.p
                    variants={letterVariants}
                    className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl text-navy mt-1 sm:mt-2 md:mt-3 lg:mt-4 xl:mt-4 2xl:mt-4"
                  >
                    Tešíme sa na Váš email!
                  </motion.p>
                </motion.div>
                <motion.img
                  src="https://img.icons8.com/fluency/96/email.png"
                  alt="Email"
                  className="h-20 sm:h-24 md:h-28 lg:h-28 xl:h-32 2xl:h-32 drop-shadow-xl"
                  animate={{ y: [0, -20, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.img
          src="/assets/ruksak.svg"
          alt="Children"
          className="absolute bottom-6 sm:bottom-8 md:bottom-10 lg:bottom-12 xl:bottom-14 2xl:bottom-16 left-4 sm:left-8 md:left-12 lg:left-16 xl:left-16 2xl:left-36 h-5 sm:h-6 md:h-8 lg:h-8 xl:h-10 2xl:h-20 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.img
          src="/assets/lopty.svg"
          alt="Children"
          className="absolute top-6 sm:top-8 md:top-10 lg:top-12 xl:top-14 2xl:top-16 right-4 sm:right-8 md:right-12 lg:right-16 xl:right-16 2xl:right-20 h-12 sm:h-16 md:h-20 lg:h-24 xl:h-28 2xl:h-32 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.div
          className="absolute top-4 sm:top-6 md:top-8 lg:top-8 xl:top-10 2xl:top-10 left-4 sm:left-6 md:left-8 lg:left-10 xl:left-10 2xl:left-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-primary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-4 sm:bottom-6 md:bottom-8 lg:bottom-8 xl:bottom-10 2xl:bottom-10 right-4 sm:right-6 md:right-8 lg:right-10 xl:right-10 2xl:right-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-secondary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </div>

      {/* Aktivity Section */}
      <div
        id="aktivity"
        className="min-h-[80vh] md:h-screen flex items-center justify-center sticky top-0 relative overflow-x-hidden overflow-y-hidden py-4 sm:py-6 md:py-8"
      >
        <motion.div
          className="text-center z-10 px-4 sm:px-6 md:px-8 w-full max-w-[90vw] sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-5xl 2xl:max-w-6xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <motion.h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-8xl font-bold uppercase tracking-tight text-primary drop-shadow-2xl"
            style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
            initial={{ scale: 0.5 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
          >
            Aktivity
          </motion.h2>
          <motion.div
            className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 xl:mt-10 2xl:mt-12 relative"
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="bg-white p-4 sm:p-6 md:p-8 lg:p-10 xl:p-8 2xl:p-8 rounded-3xl shadow-2xl border-4 border-secondary w-full max-w-[95%] sm:max-w-[90%] md:max-w-[80%] lg:max-w-[75%] xl:max-w-[70%] 2xl:max-w-[55vw] mx-auto flex flex-col sm:flex-row items-center gap-4 sm:gap-6 md:gap-8 lg:gap-10 xl:gap-12 2xl:gap-12">
              <motion.div
                className="absolute -top-4 sm:-top-6 md:-top-6 lg:-top-8 xl:-top-8 2xl:-top-8 -left-4 sm:-left-6 md:-left-6 lg:-left-8 xl:-left-8 2xl:-left-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="absolute -bottom-4 sm:-bottom-6 md:-bottom-6 lg:-bottom-8 xl:-bottom-8 2xl:-bottom-8 -right-4 sm:-right-6 md:-right-6 lg:-right-8 xl:-right-8 2xl:-right-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-secondary rounded-full"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.ul className="w-full sm:w-1/2 text-left text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl space-y-2 sm:space-y-3 md:space-y-3 lg:space-y-4 xl:space-y-4 2xl:space-y-4">
                {[
                  "Náučné a športové hry",
                  "Maľovanie",
                  "Tanec a spev",
                  "Táborák",
                  "Turistika a výlety",
                  "Jazda na koni",
                ].map((text, index) => (
                  <motion.li
                    key={index}
                    className={`p-2 sm:p-3 md:p-4 lg:p-4 xl:p-4 2xl:p-4 rounded-xl transition-all duration-300 ${
                      highlightedItemActivity === index
                        ? "bg-secondary text-cream"
                        : "bg-cream"
                    }`}
                    onMouseEnter={() => setHighlightedItemActivity(index)}
                  >
                    {text}
                  </motion.li>
                ))}
              </motion.ul>
              <motion.div className="w-full sm:w-1/2 flex justify-center">
                <motion.img
                  src={
                    [
                      "/assets/lopty.svg",
                      "/assets/paleta.svg",
                      "/assets/reprak-01.svg",
                      "/assets/ohnisko-01.svg",
                      "/assets/ruksak.svg",
                      "/assets/zabava-01.svg",
                    ][highlightedItemActivity]
                  }
                  alt="Activity Icon"
                  className="h-24 sm:h-32 md:h-36 lg:h-40 xl:h-44 2xl:h-48 drop-shadow-xl max-w-full"
                  key={highlightedItemActivity}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
              </motion.div>
              <motion.img
                src="https://img.icons8.com/fluency/48/star.png"
                alt="Star"
                className="absolute top-1 sm:top-1 md:top-2 lg:top-2 xl:top-2 2xl:top-2 right-1 sm:right-1 md:right-2 lg:right-2 xl:right-2 2xl:right-2 h-5 sm:h-6 md:h-6 lg:h-8 xl:h-8 2xl:h-8"
                animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.img
          src="/assets/gitara.svg"
          alt="Children"
          className="absolute bottom-6 sm:bottom-8 md:bottom-10 lg:bottom-12 xl:bottom-14 2xl:bottom-16 left-4 sm:left-8 md:left-12 lg:left-16 xl:left-16 2xl:left-32 h-5 sm:h-6 md:h-8 lg:h-8 xl:h-10 2xl:h-20 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.img
          src="/assets/lopty.svg"
          alt="Children"
          className="absolute top-6 sm:top-8 md:top-10 lg:top-12 xl:top-14 2xl:top-16 right-4 sm:right-8 md:right-12 lg:right-16 xl:right-16 2xl:right-20 h-12 sm:h-16 md:h-20 lg:h-24 xl:h-28 2xl:h-32 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.div
          className="absolute top-4 sm:top-6 md:top-8 lg:top-8 xl:top-10 2xl:top-10 left-4 sm:left-6 md:left-8 lg:left-10 xl:left-10 2xl:left-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-primary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-4 sm:bottom-6 md:bottom-8 lg:bottom-8 xl:bottom-10 2xl:bottom-10 right-4 sm:right-6 md:right-8 lg:right-10 xl:right-10 2xl:right-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-secondary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </div>

      {/* Kde nás nájdeš Section */}
      <div
        id="kde-nas-najdes"
        className="min-h-[80vh] md:h-screen flex items-center justify-center sticky top-0 relative overflow-x-hidden overflow-y-hidden py-4 sm:py-6 md:py-8 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/assets/pastoracne_centrum.svg')`,
        }}
      >
        <div className="absolute inset-0 z-0"></div>

        <motion.div
          className="text-center z-10 px-4 sm:px-6 md:px-8 w-full max-w-[90vw] sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-5xl 2xl:max-w-6xl relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={sectionVariants}
        >
          <motion.h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-8xl font-bold uppercase tracking-tight text-primary drop-shadow-2xl"
            style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
            initial={{ scale: 0.5 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
          >
            Kde nás nájdeš
          </motion.h2>
          <motion.div
            className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 xl:mt-10 2xl:mt-12 relative"
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="bg-white p-4 sm:p-6 md:p-8 lg:p-10 xl:p-8 2xl:p-8 rounded-3xl shadow-2xl border-4 border-primary w-full max-w-[95%] sm:max-w-[90%] md:max-w-[85%] lg:max-w-[80%] xl:max-w-[75%] 2xl:max-w-4xl mx-auto relative">
              <motion.div
                className="absolute -top-4 sm:-top-6 md:-top-6 lg:-top-8 xl:-top-8 2xl:-top-8 -left-4 sm:-left-6 md:-left-6 lg:-left-8 xl:-left-8 2xl:-left-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="absolute -bottom-4 sm:-bottom-6 md:-bottom-6 lg:-bottom-8 xl:-bottom-8 2xl:-bottom-8 -right-4 sm:-right-6 md:-right-6 lg:-right-8 xl:-right-8 2xl:-right-8 w-8 sm:w-12 md:w-12 lg:w-14 xl:w-14 2xl:w-16 h-8 sm:h-12 md:h-12 lg:h-14 xl:h-14 2xl:h-16 bg-secondary rounded-full"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              <motion.div
                className="flex flex-col items-center space-y-4 sm:space-y-6 md:space-y-8 lg:space-y-10 xl:space-y-10 2xl:space-y-12"
                variants={textVariants}
                initial="initial"
                animate="animate"
              >
                <motion.p
                  variants={letterVariants}
                  className="text-base sm:text-lg md:text-xl font-bold text-primary"
                >
                  <a href="https://www.dieceznecentrum.sk/">
                    Miesto: Diecézne centrum Zubák – krásne miesto plné zábavy!
                  </a>
                </motion.p>

                <motion.p
                  variants={letterVariants}
                  className="text-sm sm:text-base md:text-lg"
                >
                  Zubák 140, 020 64 Zubák, Slovensko
                </motion.p>

                <motion.iframe
                  variants={letterVariants}
                  src="https://www.google.com/maps?q=Diecézne+pastoračné+centrum+svätého+Vendelína+Zubák+140+02064+Zubák&output=embed"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-xl shadow-lg w-full max-w-[90%]"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.img
          src="/assets/medved.svg"
          alt="Children"
          className="absolute bottom-6 sm:bottom-8 md:bottom-10 lg:bottom-12 xl:bottom-14 2xl:bottom-16 left-4 sm:left-8 md:left-12 lg:left-16 xl:left-16 2xl:left-32 h-5 sm:h-6 md:h-8 lg:h-8 xl:h-10 2xl:h-20 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.img
          src="/assets/lopty.svg"
          alt="Children"
          className="absolute top-6 sm:top-8 md:top-10 lg:top-12 xl:top-14 2xl:top-16 right-4 sm:right-8 md:right-12 lg:right-16 xl:right-16 2xl:right-20 h-12 sm:h-16 md:h-20 lg:h-24 xl:h-28 2xl:h-32 drop-shadow-xl"
          variants={childVariants}
          animate="animate"
        />
        <motion.div
          className="absolute top-4 sm:top-6 md:top-8 lg:top-8 xl:top-10 2xl:top-10 left-4 sm:left-6 md:left-8 lg:left-10 xl:left-10 2xl:left-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-primary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-4 sm:bottom-6 md:bottom-8 lg:bottom-8 xl:bottom-10 2xl:bottom-10 right-4 sm:right-6 md:right-8 lg:right-10 xl:right-10 2xl:right-10 w-6 sm:w-8 md:w-10 lg:w-10 xl:w-12 2xl:w-12 h-6 sm:h-8 md:h-10 lg:h-10 xl:h-12 2xl:h-12 bg-secondary/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </div>
    </section>
  );
};

export default InfoContact;
